import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { X, SkipBack, SkipForward } from "lucide-react";

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

const uriFor = (track) =>
  track.kind === "album" ? `spotify:album:${track.spotify_id}` : `spotify:track:${track.spotify_id}`;

export function PlayerProvider({ children }) {
  const controllerRef = useRef(null);
  const pendingRef = useRef(null);
  const queueRef = useRef([]);
  const indexRef = useRef(0);
  const endedGuard = useRef(false);

  const [ready, setReady] = useState(false);
  const [active, setActive] = useState(false);
  const [current, setCurrent] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isShuffle, setIsShuffle] = useState(false);
  const [queueInfo, setQueueInfo] = useState({ index: 0, total: 0 });

  const loadIndex = useCallback((i) => {
    const q = queueRef.current;
    if (!q.length) return;
    const idx = ((i % q.length) + q.length) % q.length;
    indexRef.current = idx;
    const item = q[idx];
    const uri = uriFor(item.track);
    endedGuard.current = false;
    setCurrent({ ...item.track, uri, playlistTitle: item.playlist?.title, playlistId: item.playlist?.id });
    setActive(true);
    setQueueInfo({ index: idx, total: q.length });
    const c = controllerRef.current;
    if (c) {
      c.loadUri(uri);
      c.play();
    } else {
      pendingRef.current = uri;
    }
  }, []);

  const next = useCallback(() => loadIndex(indexRef.current + 1), [loadIndex]);
  const prev = useCallback(() => loadIndex(indexRef.current - 1), [loadIndex]);

  useEffect(() => {
    if (!document.getElementById("spotify-iframe-api")) {
      const s = document.createElement("script");
      s.id = "spotify-iframe-api";
      s.src = "https://open.spotify.com/embed/iframe-api/v1";
      s.async = true;
      document.body.appendChild(s);
    }

    window.onSpotifyIframeApiReady = (IFrameAPI) => {
      const el = document.getElementById("spotify-embed-slot");
      if (!el) return;
      IFrameAPI.createController(
        el,
        { width: "100%", height: 80, uri: "spotify:track:0PivOoKM1sECNTsZ7K6N3h" },
        (controller) => {
          controllerRef.current = controller;
          setReady(true);
          controller.addListener("playback_update", (e) => {
            if (!e || !e.data) return;
            const { isPaused, position, duration } = e.data;
            setIsPlaying(!isPaused);
            if (duration > 0 && position >= duration - 800 && !endedGuard.current) {
              endedGuard.current = true;
              if (queueRef.current.length > 1) next();
            }
            if (duration > 0 && position < duration - 1500) endedGuard.current = false;
          });
          if (pendingRef.current) {
            const uri = pendingRef.current;
            pendingRef.current = null;
            controller.loadUri(uri);
            controller.play();
          }
        }
      );
    };
    return () => {};
  }, [next]);

  // Play a single track (queue = whole playlist starting at that track)
  const playTrack = useCallback(
    (track, playlist) => {
      setIsShuffle(false);
      const tracks = playlist?.tracks || [track];
      const startIdx = Math.max(0, tracks.findIndex((t) => t.spotify_id === track.spotify_id && t.title === track.title));
      queueRef.current = tracks.map((t) => ({ track: t, playlist }));
      loadIndex(startIdx);
    },
    [loadIndex]
  );

  // Shuffle across all playlists
  const shuffleAll = useCallback(
    (playlists) => {
      const all = [];
      (playlists || []).forEach((pl) => (pl.tracks || []).forEach((t) => all.push({ track: t, playlist: pl })));
      for (let i = all.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [all[i], all[j]] = [all[j], all[i]];
      }
      queueRef.current = all;
      setIsShuffle(true);
      loadIndex(0);
    },
    [loadIndex]
  );

  const close = useCallback(() => {
    const c = controllerRef.current;
    if (c) c.pause();
    setActive(false);
    setIsPlaying(false);
    setIsShuffle(false);
    queueRef.current = [];
  }, []);

  const value = { ready, current, isPlaying, active, isShuffle, queueInfo, playTrack, shuffleAll, next, prev, close };

  return (
    <PlayerContext.Provider value={value}>
      {children}

      <div
        data-testid="now-playing-bar"
        className="fixed bottom-0 left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-[#242424] transition-transform duration-500"
        style={{ transform: active ? "translateY(0)" : "translateY(120%)" }}
      >
        <div className="mx-auto max-w-[1400px] px-4 md:px-10 py-3 flex items-center gap-3 md:gap-4">
          <div className="hidden sm:block min-w-0 w-44 md:w-52 shrink-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#7a7a7a] truncate">
              {isShuffle ? "Shuffle · All Playlists" : current?.playlistTitle || "Now Playing"}
            </div>
            <div className="text-sm text-[#FAFAFA] truncate font-medium">{current?.title}</div>
            <div className="text-xs text-[#8a8a8a] truncate">{current?.artist}</div>
          </div>

          <button
            data-testid="now-playing-prev"
            onClick={prev}
            aria-label="Previous track"
            className="shrink-0 h-9 w-9 flex items-center justify-center text-[#a3a3a3] hover:text-[#FAFAFA] transition-colors"
          >
            <SkipBack size={16} fill="currentColor" />
          </button>

          <div className="flex-1 min-w-0">
            <div id="spotify-embed-slot" />
          </div>

          <button
            data-testid="now-playing-next"
            onClick={next}
            aria-label="Next track"
            className="shrink-0 h-9 w-9 flex items-center justify-center text-[#a3a3a3] hover:text-[#FAFAFA] transition-colors"
          >
            <SkipForward size={16} fill="currentColor" />
          </button>

          {current?.uri && (
            <a
              href={`https://open.spotify.com/${current.uri.replace("spotify:", "").replace(":", "/")}`}
              target="_blank"
              rel="noreferrer"
              data-testid="now-playing-open-spotify"
              className="shrink-0 hidden md:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#a3a3a3] hover:text-[#1DB954] transition-colors"
            >
              Open ↗
            </a>
          )}

          <button
            data-testid="now-playing-close"
            onClick={close}
            aria-label="Close player"
            className="shrink-0 h-9 w-9 flex items-center justify-center text-[#a3a3a3] hover:text-[#FAFAFA] transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </PlayerContext.Provider>
  );
}
